import { AlertTriangle, Edit01, Plus, Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { useBlocklistRules, useDeleteBlocklistRule } from "@/api/blocklist-rules";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RuleBuilderDrawer } from "./rule-builder-drawer";

interface BlocklistRulesPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BlocklistRulesPanel({ open, onOpenChange }: BlocklistRulesPanelProps) {
  const [view, setView] = useState<"list" | "builder">("list");
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<{ ruleId: string; name: string } | undefined>(undefined);

  const { data } = useBlocklistRules();
  const deleteRule = useDeleteBlocklistRule();

  const rules = data?.items ?? [];

  const openBuilder = (ruleId?: string) => {
    setEditingRuleId(ruleId);
    setView("builder");
  };
  const backToList = () => {
    setView("list");
    setEditingRuleId(undefined);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteRule.mutateAsync(deleteTarget.ruleId);
    setDeleteTarget(undefined);
  };

  return (
    <>
      <SlideoutMenu isOpen={open && view === "list"} onOpenChange={onOpenChange} isDismissable>
        <SlideoutMenu.Header onClose={() => onOpenChange(false)}>
          <h2 className="text-lg font-semibold text-primary">Blocklist Rules</h2>
        </SlideoutMenu.Header>

        <SlideoutMenu.Content>
          {rules.length === 0 ? (
            <p className="text-sm text-tertiary">No rules yet.</p>
          ) : (
            <div className="flex flex-col divide-y divide-secondary">
              {rules.map((rule) => (
                <div key={rule.ruleId} className="flex flex-row items-center justify-between gap-3 py-3">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-medium text-primary truncate">{rule.name}</span>
                    <span className="text-xs text-tertiary">
                      {rule.blockedCount} creator{rule.blockedCount === 1 ? "" : "s"} currently blocked
                    </span>
                  </div>
                  <div className="flex flex-row items-center gap-2 shrink-0">
                    <Badge type="pill-color" size="sm" color={rule.isActive ? "success" : "gray"}>
                      {rule.isActive ? "Enabled" : "Disabled"}
                    </Badge>
                    <Button color="secondary" size="sm" iconLeading={Edit01} onClick={() => openBuilder(rule.ruleId)}>
                      Edit
                    </Button>
                    <button
                      type="button"
                      className="p-1.5 rounded text-quaternary hover:text-error-primary hover:bg-primary_hover transition-colors"
                      onClick={() => setDeleteTarget({ ruleId: rule.ruleId, name: rule.name })}
                      aria-label={`Delete ${rule.name}`}
                    >
                      <Trash01 className="size-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SlideoutMenu.Content>

        <SlideoutMenu.Footer>
          <Button color="primary" iconLeading={Plus} onClick={() => openBuilder(undefined)}>
            New Rule
          </Button>
        </SlideoutMenu.Footer>
      </SlideoutMenu>

      <RuleBuilderDrawer
        open={open && view === "builder"}
        onOpenChange={(isOpen) => {
          if (!isOpen) backToList();
        }}
        ruleId={editingRuleId}
        onBack={backToList}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(isOpen) => !isOpen && setDeleteTarget(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? Creators only blocked by this rule
              will be unblocked. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2.5 rounded-md bg-utility-error-50 border border-utility-error-200 px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 text-utility-error-600 shrink-0 mt-0.5" />
            <p className="text-xs text-utility-error-700">Creators still matching another active rule stay blocked.</p>
          </div>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setDeleteTarget(undefined)}>
              Cancel
            </Button>
            <Button color="primary-destructive" size="sm" onClick={handleDelete} isLoading={deleteRule.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
